import { EventStatus, EventType } from "@/types";
import { formatEventType } from "@/utils";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { FC, useState } from "react";
import { useListContext } from "react-admin";
import { z } from "zod";
import { EventsFilterProps } from "./events-filter.types";

const initialFormState = {
  statuses: [] as EventStatus[],
  types: [] as EventType[],
  fromDate: "",
  toDate: "",
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formSchema = z.object({
  statuses: z.array(z.nativeEnum(EventStatus)),
  types: z.array(z.nativeEnum(EventType)),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const EventsFilter: FC<EventsFilterProps> = ({ onModalClose }) => {
  const { filterValues, setFilters } = useListContext();
  const [filtersFormData, setFiltersFormData] = useState<Partial<FormData>>({});

  const formData: FormData = {
    statuses:
      filtersFormData.statuses ??
      filterValues.statuses ??
      initialFormState.statuses,
    types:
      filtersFormData.types ?? filterValues.types ?? initialFormState.types,
    fromDate: filtersFormData.fromDate ?? filterValues.fromDate ?? "",
    toDate: filtersFormData.toDate ?? filterValues.toDate ?? "",
  };

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: "statuses" | "types"
  ) => {
    const checked = event.target.checked;
    const value = event.target.name as EventStatus | EventType;
    let currentArray = [...formData[field]];

    if (checked) {
      if (!currentArray.includes(value)) {
        currentArray.push(value);
      }
    } else {
      currentArray = currentArray.filter((v) => v !== value);
    }

    setFiltersFormData({ ...formData, [field]: currentArray });
  };

  const onSubmit = () => {
    const filters = {
      statuses: formData.statuses.length ? formData.statuses : undefined,
      types: formData.types.length ? formData.types : undefined,
      fromDate: formData.fromDate || undefined,
      toDate: formData.toDate || undefined,
    };

    setFilters({ ...filterValues, ...filters });
    onModalClose();
  };

  return (
    <Stack spacing={2}>
      <Divider sx={{ borderColor: "#333" }} />

      <FormGroup>
        <Typography variant="h6" align="left">
          Status
        </Typography>
        <FormControlLabel
          name={EventStatus.ACTIVE}
          label="Active"
          labelPlacement="start"
          sx={{ ml: 0, justifyContent: "space-between" }}
          control={
            <Checkbox
              checked={formData.statuses.includes(EventStatus.ACTIVE)}
              onChange={(e) => handleCheckboxChange(e, "statuses")}
            />
          }
        />
        <FormControlLabel
          name={EventStatus.CANCELLED}
          label="Cancelled"
          labelPlacement="start"
          sx={{ ml: 0, justifyContent: "space-between" }}
          control={
            <Checkbox
              checked={formData.statuses.includes(EventStatus.CANCELLED)}
              onChange={(e) => handleCheckboxChange(e, "statuses")}
            />
          }
        />
      </FormGroup>

      <Divider sx={{ borderColor: "#333" }} />

      <FormGroup>
        <Typography variant="h6" align="left">
          Type
        </Typography>
        <FormControlLabel
          name={EventType.DATE_NIGHT}
          label={formatEventType(EventType.DATE_NIGHT)}
          labelPlacement="start"
          sx={{ ml: 0, justifyContent: "space-between" }}
          control={
            <Checkbox
              checked={formData.types.includes(EventType.DATE_NIGHT)}
              onChange={(e) => handleCheckboxChange(e, "types")}
            />
          }
        />
        <FormControlLabel
          name={EventType.AFFAIR}
          label={formatEventType(EventType.AFFAIR)}
          labelPlacement="start"
          sx={{ ml: 0, justifyContent: "space-between" }}
          control={
            <Checkbox
              checked={formData.types.includes(EventType.AFFAIR)}
              onChange={(e) => handleCheckboxChange(e, "types")}
            />
          }
        />
      </FormGroup>

      <Divider sx={{ borderColor: "#333" }} />

      <Stack spacing={2}>
        <DatePicker
          label="From Date"
          disableFuture={false}
          value={formData.fromDate ? dayjs(formData.fromDate) : null}
          onChange={(date) =>
            setFiltersFormData({
              ...formData,
              fromDate: date ? date.startOf("day").toISOString() : undefined,
            })
          }
        />
        <DatePicker
          label="To Date"
          disablePast={false}
          value={formData.toDate ? dayjs(formData.toDate) : null}
          onChange={(date) =>
            setFiltersFormData({
              ...formData,
              toDate: date ? date.endOf("day").toISOString() : undefined,
            })
          }
        />
      </Stack>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          onClick={() => {
            setFiltersFormData(initialFormState);
          }}
        >
          Reset
        </Button>

        <Stack direction={"row"} spacing={2}>
          <Button variant="outlined" onClick={onModalClose}>
            Cancel
          </Button>
          <Button variant="contained" color="secondary" onClick={onSubmit}>
            Filter
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

export { EventsFilter };
